import React, { useMemo, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsTimeline from 'highcharts/modules/timeline';
import { TimelineChartOrientation } from '@opengov/stories-shared/types';
import { ProgressTrackerSettingItemProps } from './ProgressTrackerSettingItem';
import { SHARED_TIMELINE_CHART_CONFIG } from '@opengov/stories-shared/constants/timelineChart';
import { getVerticalTimelineHeight } from '@opengov/stories-shared/utils/progressTracker';
import { styles } from './ProgressTrackerPreview.styles';
import { HorizontalLabelPosition, VerticalLabelPosition } from './context/ProgressTracker.Provider';
HighchartsTimeline(Highcharts);

interface ProgressTrackerPreviewProps {
  settings: ProgressTrackerSettingItemProps[];
  orientation?: TimelineChartOrientation;
}


export const ProgressTrackerPreview: React.FC<ProgressTrackerPreviewProps> = ({
  settings,
  orientation = TimelineChartOrientation.VERTICAL
}) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const prevOrientationRef = useRef<TimelineChartOrientation | undefined>(orientation);

  // Create timeline data
  const timelineData = useMemo(() => {
    if (settings.length === 0) return [];

    return settings.map((setting, index) => {
      const getLabelPosition = () => {
        if (orientation === TimelineChartOrientation.VERTICAL) {
          // Vertical timeline - labels on left or right side
          return {
            align: !setting.verticalLabelPosition ? (index % 2 === 0 ? 'right' : 'left') : setting.verticalLabelPosition === VerticalLabelPosition.Left ? 'right' : 'left',
            verticalAlign: 'middle',
            x: !setting.verticalLabelPosition ? (index % 2 === 0 ? -30 : 30) : setting.verticalLabelPosition === VerticalLabelPosition.Left ? -30 : 30,
            y: 0
          };
        } else {
          // Horizontal timeline - labels on top or bottom
          return {
            align: 'center',
            verticalAlign: !setting.horizontalLabelPosition ? (index % 2 === 0 ? 'bottom' : 'top') : setting.horizontalLabelPosition === HorizontalLabelPosition.Top ? 'bottom' : 'top',
            x: 0,
            y: !setting.horizontalLabelPosition ? (index % 2 === 0 ? -30 : 30) : setting.horizontalLabelPosition === HorizontalLabelPosition.Top ? -30 : 30,
          };
        }
      };
      //to capitalize  the status
      const getStatus = () => {
        return setting.status ?
          setting.status.replace(/\w\S*/g, w => w[0].toUpperCase() + w.slice(1).toLowerCase()) : '';
      }
      return {
        x: index,
        y: 1,
        name: getStatus(),
        color: setting.colorCode || '#616365', // Default color if undefined
        status: setting.status || '',
        dataLabels: getLabelPosition()
      };
    });
  }, [settings, orientation]);

  // Base chart options using Highcharts.merge
  const chartOptions = useMemo(() => {
    if (settings.length === 0) return null;

    return Highcharts.merge(true, {}, SHARED_TIMELINE_CHART_CONFIG, {
      title: { text: "" },
      accessibility: {
        enabled: false
      },
      chart: {
        type: "timeline",
        ...(orientation === TimelineChartOrientation.VERTICAL
          ? {
            height: getVerticalTimelineHeight(
              settings.map(s => ({
                id: s.id ?? "",
                title: s.status ?? "",
                description: s.status ?? "",
                status: {
                  id: s.id ?? "",
                  title: s.status ?? ""
                }
              }))
            ),
            inverted: true
          }
          : {
            inverted: false,
            height: 200
          }),
        events: {
          load: function () {
            this.update({ accessibility: { enabled: true } }, false);
            this.redraw();
          }
        }
      },
      tooltip:
        orientation === TimelineChartOrientation.VERTICAL
          ? { enabled: false }
          : {
            enabled: false,
            headerFormat: "",
            pointFormat: "{point.description}"
          },
      series: [
        {
          ...(SHARED_TIMELINE_CHART_CONFIG.series?.[0] || {}),
          type: "timeline",
          showInLegend: false,
          dataLabels:
            orientation === TimelineChartOrientation.VERTICAL
              ? Highcharts.merge(
                (SHARED_TIMELINE_CHART_CONFIG.series?.[0] || {}).dataLabels || {},
                {
                  enabled: true,
                  useHTML: true,
                  format:
                    '<div style="display:inline-block;width:max-content;  max-width:300px; white-space:wrap;overflow-wrap:anywhere;line-height:1.2;">' +
                    '<b>{point.name}</b><br/>{point.description}' +
                    '</div>',
                  style: {
                    fontSize: '16px',
                    fontWeight: 'normal'
                  }
                }
              )
              : Highcharts.merge(
                (SHARED_TIMELINE_CHART_CONFIG.series?.[0] || {}).dataLabels || {},
                {
                  enabled: true,
                  useHTML: true,
                  format:
                    '<div style="display:inline-block;width:max-content; ; max-width:90px; white-space:wrap;overflow-wrap:anywhere;line-height:1.2;">{point.name}</div>',
                  style: {
                    fontSize: '16px',
                    fontWeight: 'bold',
                    whiteSpace: 'wrap',
                  }
                }
              ),
          data: timelineData
        } as Highcharts.SeriesTimelineOptions
      ]
    } as Highcharts.Options);
  }, [timelineData, settings]);

  // Update chart data when settings change (but orientation doesn't)
  useEffect(() => {
    if (chartRef.current?.chart && prevOrientationRef.current === orientation) {
      // Only update data, not full chart config
      const chart = chartRef.current.chart;
      if (chart.series && chart.series[0]) {
        chart.series[0].setData(timelineData, true); // true = redraw
      }
    }
    prevOrientationRef.current = orientation;
  }, [timelineData, orientation]);

  //Key only changes when orientation changes to force full remount
  const chartKey = useMemo(() => {
    return orientation;
  }, [orientation]);

  if (settings.length === 0) {
    return (
      <Box sx={styles.progressTrackerPreviewEmptyContainer}>
        <Typography variant="body2" color="text.secondary">
          No progress tracker settings configured yet
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={styles.progressTrackerPreviewContainer}>
      {chartOptions && (
        <HighchartsReact
          ref={chartRef}
          key={chartKey}
          highcharts={Highcharts}
          options={chartOptions}
        />
      )}
    </Box>
  );
};
